import { Controller } from '@hotwired/stimulus'

export class SelectController extends Controller<HTMLDivElement> {
  declare trigger: HTMLButtonElement | null
  declare selectedLabel?: HTMLSpanElement | null
  declare popover: HTMLElement | null
  declare listbox: HTMLElement | null
  declare input: HTMLInputElement | null
  declare filter: HTMLInputElement | null
  declare allOptions: HTMLElement[]
  declare options: HTMLElement[]
  declare visibleOptions: HTMLElement[]
  declare activeIndex: number
  declare selectedOptions: Set<HTMLElement> | null
  declare placeholder: string | null
  declare closeOnSelect: boolean
  declare isMultiple: boolean

  connect(): void {
    this.trigger = this.element.querySelector(':scope > button')
    this.selectedLabel = this.trigger?.querySelector(':scope > span')
    this.popover = this.element.querySelector(':scope > [data-popover]')
    this.listbox = this.popover
      ? this.popover.querySelector('[role="listbox"]')
      : null
    this.input = this.element.querySelector(':scope > input[type="hidden"]')
    this.filter = this.element.querySelector('header input[type="text"]')

    if (!this.trigger || !this.popover || !this.listbox || !this.input) {
      const missing = []
      if (!this.trigger) missing.push('trigger')
      if (!this.popover) missing.push('popover')
      if (!this.listbox) missing.push('listbox')
      if (!this.input) missing.push('input')
      console.error(
        `Select component initialisation failed. Missing element(s): ${missing.join(', ')}`,
        this.element
      )
      return
    }

    this.allOptions = Array.from(
      this.listbox.querySelectorAll('[role="option"]')
    )
    this.options = this.allOptions.filter(
      opt => opt.getAttribute('aria-disabled') !== 'true'
    )
    this.visibleOptions = [...this.options]
    this.activeIndex = -1
    this.isMultiple =
      this.listbox.getAttribute('aria-multiselectable') === 'true'
    this.selectedOptions = this.isMultiple ? new Set() : null
    this.placeholder = this.isMultiple
      ? this.element.dataset.placeholder || ''
      : null
    this.closeOnSelect = this.element.dataset.closeOnSelect === 'true'

    // Watch for listbox content changes (e.g. from Turbo Stream updates)
    const listboxObserver = new MutationObserver(() => {
      this.refreshOptions()
    })
    listboxObserver.observe(this.popover, { childList: true, subtree: true })

    if (this.filter) {
      const filterOptions = () => {
        const searchTerm = this.filter?.value?.trim().toLowerCase() || ''

        this.setActiveOption(-1)

        this.visibleOptions = []
        this.allOptions.forEach(option => {
          if (option.hasAttribute('data-force')) {
            option.setAttribute('aria-hidden', 'false')
            if (this.options.includes(option)) {
              this.visibleOptions.push(option)
            }
            return
          }

          const optionText = (option.dataset.filter || option.textContent)
            .trim()
            .toLowerCase()
          const keywordList = (option.dataset.keywords || '')
            .toLowerCase()
            .split(/[\s,]+/)
            .filter(Boolean)
          const matchesKeyword = keywordList.some(keyword =>
            keyword.includes(searchTerm)
          )
          const matches = optionText.includes(searchTerm) || matchesKeyword
          option.setAttribute('aria-hidden', String(!matches))
          if (matches && this.options.includes(option)) {
            this.visibleOptions.push(option)
          }
        })
      }

      this.filter?.addEventListener('input', filterOptions)
    }

    // Initialization
    if (this.isMultiple) {
      const ariaSelected = this.options.filter(
        opt => opt.getAttribute('aria-selected') === 'true'
      )
      try {
        const parsed = JSON.parse(this.input?.value || '[]')
        const validValues = new Set(this.options.map(this.getValue))
        const initialValues = Array.isArray(parsed)
          ? parsed.filter(v => validValues.has(v))
          : []

        const initialOptions: HTMLElement[] = []
        if (initialValues.length > 0) {
          // Match values to options in order, allowing duplicates
          initialValues.forEach(val => {
            const opt = this.options.find(
              o => this.getValue(o) === val && !initialOptions.includes(o)
            )
            if (opt) initialOptions.push(opt)
          })
        } else {
          initialOptions.push(...ariaSelected)
        }

        this.updateValue(initialOptions, false)
      } catch (e) {
        this.updateValue(ariaSelected, false)
      }
    } else {
      const initialOption =
        this.options.find(opt => this.getValue(opt) === this.input?.value) ||
        this.options[0]
      if (initialOption) this.updateValue(initialOption, false)
    }

    // Use popover for event delegation since listbox may be replaced by Turbo Stream
    this.popover.addEventListener('mousemove', event => {
      const option: HTMLElement | null = (event.target as HTMLElement).closest(
        '[role="option"]'
      )
      if (option && this.visibleOptions.includes(option)) {
        const index = this.options.indexOf(option)
        if (index !== this.activeIndex) {
          this.setActiveOption(index)
        }
      }
    })

    this.popover.addEventListener('mouseleave', () => {
      const currentListbox = this.popover?.querySelector('[role="listbox"]')
      const selectedOption = currentListbox?.querySelector(
        '[role="option"][aria-selected="true"]'
      )
      if (selectedOption) {
        this.setActiveOption(
          this.options.indexOf(selectedOption as HTMLElement)
        )
      } else {
        this.setActiveOption(-1)
      }
    })

    this.trigger?.addEventListener('keydown', this.handleKeyNavigation)
    if (this.filter) {
      this.filter.addEventListener('keydown', this.handleKeyNavigation)
    }

    this.trigger?.addEventListener('click', () => {
      const isExpanded = this.trigger?.getAttribute('aria-expanded') === 'true'
      if (isExpanded) {
        this.closePopover()
      } else {
        this.openPopover()
      }
    })

    this.popover?.addEventListener('click', event => {
      const clickedOption: HTMLElement | null = (
        event.target as HTMLElement
      ).closest('[role="option"]')
      if (!clickedOption) return

      const option = this.options.find(opt => opt === clickedOption)
      if (!option) return

      if (this.isMultiple) {
        this.toggleMultipleValue(option)
        if (this.closeOnSelect) {
          this.closePopover()
        } else {
          this.setActiveOption(this.options.indexOf(option))
          if (this.filter) {
            this.filter?.focus()
          } else {
            this.trigger?.focus()
          }
        }
      } else {
        if (this.input?.value !== this.getValue(option)) {
          this.updateValue(option)
        }
        this.closePopover()
      }
    })

    document.addEventListener('click', event => {
      if (!this.element.contains(event.target as Node)) {
        this.closePopover(false)
      }
    })

    document.addEventListener('basecoat:popover', event => {
      if ((event as CustomEvent).detail.source !== this.element) {
        this.closePopover(false)
      }
    })

    this.popover?.setAttribute('aria-hidden', 'true')

    // Public API
    Object.defineProperty(this.element, 'value', {
      get: () => {
        if (this.isMultiple) {
          return this.options
            .filter(opt => this.selectedOptions?.has(opt))
            .map(this.getValue)
        } else {
          return this.input?.value
        }
      },
      set: val => {
        if (this.isMultiple) {
          const values = Array.isArray(val) ? val : val != null ? [val] : []
          const opts: HTMLElement[] = []
          values.forEach(v => {
            const opt = this.options.find(
              o => this.getValue(o) === v && !opts.includes(o)
            )
            if (opt) opts.push(opt)
          })
          this.updateValue(opts)
        } else {
          const option = this.options.find(opt => this.getValue(opt) === val)
          if (option) {
            this.updateValue(option as HTMLElement)
            this.closePopover()
          }
        }
      },
    })

    // selectComponent.select = select
    // selectComponent.selectByValue = select // Backward compatibility alias
    // if (isMultiple) {
    //   selectComponent.deselect = deselect
    //   selectComponent.toggle = toggle
    //   selectComponent.selectAll = () => updateValue(options)
    //   selectComponent.selectNone = () => updateValue([])
    // }
    // selectComponent.dataset.selectInitialized = true
    // selectComponent.dispatchEvent(new CustomEvent('basecoat:initialized'))
  }

  getValue = (opt: HTMLElement) => opt.dataset.value ?? opt.textContent.trim()

  refreshOptions = () => {
    // Re-query the listbox since Turbo Stream may have replaced it
    const currentListbox = this.popover?.querySelector('[role="listbox"]')
    if (!currentListbox) return

    this.allOptions = Array.from(
      currentListbox.querySelectorAll('[role="option"]')
    )
    this.options = this.allOptions.filter(
      opt => opt.getAttribute('aria-disabled') !== 'true'
    )
    this.visibleOptions = [...this.options]
    this.setActiveOption(-1)
  }

  setActiveOption = (index: number) => {
    if (this.activeIndex > -1 && this.options[this.activeIndex]) {
      this.options[this.activeIndex].classList.remove('active')
    }

    this.activeIndex = index

    if (this.activeIndex > -1) {
      const activeOption = this.options[this.activeIndex]
      activeOption.classList.add('active')
      if (activeOption.id) {
        this.trigger?.setAttribute('aria-activedescendant', activeOption.id)
      } else {
        this.trigger?.removeAttribute('aria-activedescendant')
      }
    } else {
      this.trigger?.removeAttribute('aria-activedescendant')
    }
  }

  hasTransition = (): boolean => {
    if (!this.popover) return false

    const style = getComputedStyle(this.popover)

    return (
      parseFloat(style.transitionDuration) > 0 ||
      parseFloat(style.transitionDelay) > 0
    )
  }

  updateValue = (
    optionOrOptions: HTMLElement | HTMLElement[],
    triggerEvent = true
  ) => {
    let value: string | string[]

    if (this.isMultiple) {
      const opts = Array.isArray(optionOrOptions) ? optionOrOptions : []
      this.selectedOptions?.clear()
      opts.forEach(opt => this.selectedOptions?.add(opt))

      // Get selected options in DOM order
      const selected = this.options.filter(opt =>
        this.selectedOptions?.has(opt)
      )
      if (selected.length === 0) {
        this.selectedLabel!.textContent = this.placeholder!
        this.selectedLabel?.classList.add('text-muted-foreground')
      } else {
        this.selectedLabel!.textContent = selected
          .map(opt => opt.dataset.label || opt.textContent.trim())
          .join(', ')
        this.selectedLabel?.classList.remove('text-muted-foreground')
      }

      value = selected.map(this.getValue)
      this.input!.value = JSON.stringify(value)
    } else {
      const option = optionOrOptions
      if (!option) return
      this.selectedLabel!.innerHTML = (option as HTMLElement).innerHTML
      value = this.getValue(option as HTMLElement)
      this.input!.value = value
    }

    this.options.forEach(opt => {
      const isSelected = this.isMultiple
        ? this.selectedOptions?.has(opt)
        : opt === optionOrOptions
      if (isSelected) {
        opt.setAttribute('aria-selected', 'true')
      } else {
        opt.removeAttribute('aria-selected')
      }
    })

    if (triggerEvent) {
      this.element.dispatchEvent(
        new CustomEvent('change', {
          detail: { value },
          bubbles: true,
        })
      )
    }
  }

  closePopover = (focusOnTrigger = true): void => {
    if (this.popover?.getAttribute('aria-hidden') === 'true') return

    if (this.filter) {
      const resetFilter = () => {
        this.filter!.value = ''
        this.visibleOptions = [...this.options]
        this.allOptions.forEach(opt => opt.setAttribute('aria-hidden', 'false'))
      }

      if (this.hasTransition()) {
        this.popover?.addEventListener('transitionend', resetFilter, {
          once: true,
        })
      } else {
        resetFilter()
      }
    }

    if (focusOnTrigger) this.trigger?.focus()
    this.popover?.setAttribute('aria-hidden', 'true')
    this.trigger?.setAttribute('aria-expanded', 'false')
    this.setActiveOption(-1)
  }

  toggleMultipleValue = (option: HTMLElement): void => {
    if (this.selectedOptions?.has(option)) {
      this.selectedOptions?.delete(option)
    } else {
      this.selectedOptions?.add(option)
    }
    this.updateValue(this.options.filter(opt => this.selectedOptions?.has(opt)))
  }

  select = (value: string): void => {
    if (this.isMultiple) {
      const option = this.options.find(
        opt => this.getValue(opt) === value && !this.selectedOptions?.has(opt)
      )
      if (!option) return
      this.selectedOptions?.add(option)
      this.updateValue(
        this.options.filter(opt => this.selectedOptions?.has(opt))
      )
    } else {
      const option = this.options.find(opt => this.getValue(opt) === value)
      if (!option) return
      if (this.input?.value !== value) {
        this.updateValue(option)
      }
      this.closePopover()
    }
  }

  deselect = (value: string): void => {
    if (!this.isMultiple) return
    const option = this.options.find(
      opt => this.getValue(opt) === value && this.selectedOptions?.has(opt)
    )
    if (!option) return
    this.selectedOptions?.delete(option)
    this.updateValue(this.options.filter(opt => this.selectedOptions?.has(opt)))
  }

  toggle = (value: string): void => {
    if (!this.isMultiple) return
    const option = this.options.find(opt => this.getValue(opt) === value)
    if (!option) return
    this.toggleMultipleValue(option)
  }

  handleKeyNavigation = (event: KeyboardEvent): void => {
    const isPopoverOpen = this.popover?.getAttribute('aria-hidden') === 'false'

    if (
      !['ArrowDown', 'ArrowUp', 'Enter', 'Home', 'End', 'Escape'].includes(
        event.key
      )
    ) {
      return
    }

    if (!isPopoverOpen) {
      if (event.key !== 'Enter' && event.key !== 'Escape') {
        event.preventDefault()
        this.trigger?.click()
      }
      return
    }

    event.preventDefault()

    if (event.key === 'Escape') {
      this.closePopover()
      return
    }

    if (event.key === 'Enter') {
      if (this.activeIndex > -1) {
        const option = this.options[this.activeIndex]
        if (this.isMultiple) {
          this.toggleMultipleValue(option)
          if (this.closeOnSelect) {
            this.closePopover()
          }
        } else {
          if (this.input?.value !== this.getValue(option)) {
            this.updateValue(option)
          }
          this.closePopover()
        }
      }
      return
    }

    if (this.visibleOptions.length === 0) return

    const currentVisibleIndex =
      this.activeIndex > -1
        ? this.visibleOptions.indexOf(this.options[this.activeIndex])
        : -1
    let nextVisibleIndex = currentVisibleIndex

    switch (event.key) {
      case 'ArrowDown':
        if (currentVisibleIndex < this.visibleOptions.length - 1) {
          nextVisibleIndex = currentVisibleIndex + 1
        }
        break
      case 'ArrowUp':
        if (currentVisibleIndex > 0) {
          nextVisibleIndex = currentVisibleIndex - 1
        } else if (currentVisibleIndex === -1) {
          nextVisibleIndex = 0
        }
        break
      case 'Home':
        nextVisibleIndex = 0
        break
      case 'End':
        nextVisibleIndex = this.visibleOptions.length - 1
        break
    }

    if (nextVisibleIndex !== currentVisibleIndex) {
      const newActiveOption = this.visibleOptions[nextVisibleIndex]
      this.setActiveOption(this.options.indexOf(newActiveOption))
      newActiveOption.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    }
  }

  openPopover = () => {
    document.dispatchEvent(
      new CustomEvent('basecoat:popover', {
        detail: { source: this.element },
      })
    )

    if (this.filter) {
      if (this.hasTransition()) {
        this.popover?.addEventListener(
          'transitionend',
          () => {
            this.filter?.focus()
          },
          { once: true }
        )
      } else {
        this.filter?.focus()
      }
    }

    this.popover?.setAttribute('aria-hidden', 'false')
    this.trigger?.setAttribute('aria-expanded', 'true')

    const selectedOption = this.listbox?.querySelector(
      '[role="option"][aria-selected="true"]'
    )
    if (selectedOption) {
      this.setActiveOption(this.options.indexOf(selectedOption as HTMLElement))
      selectedOption.scrollIntoView({ block: 'nearest' })
    }
  }
}
